import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Activity, Loader2, RefreshCw, CheckCircle2, XCircle, Shield, Globe, FileText, History, TrendingUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';

const MonitorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [monitor, setMonitor] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchMonitor();
    fetchDetails();
    fetchHistory();
  }, [id]);

  const fetchMonitor = async () => {
    try {
      const response = await api.get(`/monitors/${id}`);
      setMonitor(response.data);
    } catch (error) {
      toast.error('Failed to load monitor');
      navigate('/uptime');
    } finally {
      setLoading(false);
    }
  };

  const fetchDetails = async () => {
    setLoadingDetails(true);
    try {
      const response = await api.get(`/monitors/${id}/details`);
      setDetails(response.data);
    } catch (error) {
      console.error('Failed to load details', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get(`/monitors/${id}/history?hours=12`);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Failed to load history', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCheck = async () => {
    setChecking(true);
    try {
      await api.post(`/monitors/${id}/check`);
      toast.success('Check completed!');
      fetchMonitor();
      fetchDetails();
      fetchHistory();
    } catch (error) {
      toast.error('Check failed');
    } finally {
      setChecking(false);
    }
  };

  const formatChartData = () => {
    return history.map(item => ({
      time: new Date(item.timestamp).toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      }),
      responseTime: item.response_time || 0,
      status: item.status === 'up' ? 1 : 0,
      fullTime: new Date(item.timestamp).toLocaleString()
    }));
  };

  const getStatusIcon = (status) => {
    if (status === 'up') return <CheckCircle2 className="w-6 h-6 text-green-600" />;
    if (status === 'down') return <XCircle className="w-6 h-6 text-red-600" />;
    return <Activity className="w-6 h-6 text-yellow-600" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!monitor) return null;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/uptime')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Monitors
          </Button>

          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              {getStatusIcon(monitor.status)}
              <div>
                <h1 className="text-3xl font-bold text-slate-800">{monitor.name}</h1>
                <p className="text-slate-600 mt-1">{monitor.target}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <Badge variant="outline" className="uppercase">{monitor.monitoring_type}</Badge>
                  <Badge className={monitor.status === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                    {monitor.status.toUpperCase()}
                  </Badge>
                </div>
              </div>
            </div>

            <Button
              onClick={handleCheck}
              disabled={checking}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {checking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Run Check Now
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Response Time</p>
              <p className="text-2xl font-bold text-slate-800">
                {monitor.response_time ? `${monitor.response_time}ms` : 'N/A'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Avg Response</p>
              <p className="text-2xl font-bold text-slate-800">
                {monitor.avg_response_time ? `${monitor.avg_response_time}ms` : 'N/A'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Uptime</p>
              <p className="text-2xl font-bold text-green-600">
                {monitor.uptime_percentage?.toFixed(2) || '100'}%
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-slate-600">Total Checks</p>
              <p className="text-2xl font-bold text-slate-800">
                {monitor.total_checks || 0}
              </p>
              <p className="text-xs text-red-600">Failed: {monitor.failed_checks || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for HTTP monitors */}
        {monitor.monitoring_type === 'http' ? (
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="ssl">SSL Certificate</TabsTrigger>
              <TabsTrigger value="whois">WHOIS</TabsTrigger>
              <TabsTrigger value="records">DNS Records</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="space-y-4">
                {/* 12-Hour Performance Graph */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        <CardTitle>Performance - Last 12 Hours</CardTitle>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/uptime/${id}/history`)}
                        className="border-blue-300 text-blue-700"
                      >
                        <History className="w-4 h-4 mr-2" />
                        View Full History
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loadingHistory ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                      </div>
                    ) : history.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-slate-500">No data available yet. Run a check to start collecting data.</p>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={formatChartData()}>
                          <defs>
                            <linearGradient id="colorResponse" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis 
                            dataKey="time" 
                            stroke="#64748b"
                            style={{ fontSize: '12px' }}
                          />
                          <YAxis 
                            stroke="#64748b"
                            style={{ fontSize: '12px' }}
                            label={{ value: 'Response Time (ms)', angle: -90, position: 'insideLeft' }}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'white', 
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              padding: '8px'
                            }}
                            formatter={(value, name) => {
                              if (name === 'responseTime') return [`${value}ms`, 'Response Time'];
                              if (name === 'status') return [value === 1 ? 'Up' : 'Down', 'Status'];
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Area 
                            type="monotone" 
                            dataKey="responseTime" 
                            stroke="#3b82f6" 
                            fillOpacity={1}
                            fill="url(#colorResponse)"
                            name="Response Time (ms)"
                          />
                          <Line 
                            type="stepAfter" 
                            dataKey="status" 
                            stroke="#10b981" 
                            strokeWidth={2}
                            dot={false}
                            name="Status (Up=1, Down=0)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                {/* Monitor Details */}
                <Card>
                  <CardHeader>
                    <CardTitle>Monitor Overview</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-600">Last Check</p>
                        <p className="font-semibold">{formatDate(monitor.last_check)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Check Interval</p>
                        <p className="font-semibold">{monitor.check_interval}s</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Created</p>
                        <p className="font-semibold">{formatDate(monitor.created_at)}</p>
                      </div>
                    </div>

                    {monitor.open_ports && monitor.open_ports.length > 0 && (
                      <div>
                        <p className="text-sm text-slate-600 mb-2">Open Ports</p>
                        <div className="flex flex-wrap gap-2">
                          {monitor.open_ports.map(port => (
                            <Badge key={port} variant="outline">{port}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="ssl">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center space-x-2">
                      <Shield className="w-5 h-5 text-green-600" />
                      <span>SSL Certificate Details</span>
                    </CardTitle>
                    {monitor.ssl_days_remaining !== null && monitor.ssl_days_remaining !== undefined && (
                      <Badge 
                        className={
                          monitor.ssl_days_remaining > 180
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : monitor.ssl_days_remaining > 30
                            ? 'bg-amber-100 text-amber-700 border-amber-200'
                            : 'bg-red-100 text-red-700 border-red-200'
                        }
                      >
                        {monitor.ssl_days_remaining} days remaining
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingDetails ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : details?.ssl?.error ? (
                    <p className="text-red-600">Error: {details.ssl.error}</p>
                  ) : details?.ssl ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-600">Common Name</p>
                          <p className="font-semibold">{details.ssl.subject?.commonName || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Organization</p>
                          <p className="font-semibold">{details.ssl.subject?.organizationName || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Issuer</p>
                          <p className="font-semibold">{details.ssl.issuer?.commonName || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Serial Number</p>
                          <p className="font-mono text-xs">{details.ssl.serial_number || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Valid From</p>
                          <p className="font-semibold">{details.ssl.not_before || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Valid Until</p>
                          <p className="font-semibold">{details.ssl.not_after || 'N/A'}</p>
                        </div>
                        {details.ssl.algorithm && (
                          <div>
                            <p className="text-sm text-slate-600">Algorithm</p>
                            <p className="font-semibold">{details.ssl.algorithm}</p>
                          </div>
                        )}
                        {details.ssl.protocol && (
                          <div>
                            <p className="text-sm text-slate-600">Protocol</p>
                            <p className="font-semibold">{details.ssl.protocol}</p>
                          </div>
                        )}
                      </div>

                      {details.ssl.san && details.ssl.san.length > 0 && (
                        <div>
                          <p className="text-sm text-slate-600 mb-2">Subject Alternative Names</p>
                          <div className="bg-slate-50 p-3 rounded-lg">
                            {details.ssl.san.map((san, idx) => (
                              <p key={idx} className="text-sm font-mono">{san[1]}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-slate-500">No SSL data available</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="whois">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    <span>WHOIS Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingDetails ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : details?.whois?.error ? (
                    <p className="text-red-600">Error: {details.whois.error}</p>
                  ) : details?.whois?.raw ? (
                    <div className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
                      <pre className="text-xs font-mono whitespace-pre-wrap">{details.whois.raw}</pre>
                    </div>
                  ) : (
                    <p className="text-slate-500">No WHOIS data available</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="records">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <span>DNS Records</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingDetails ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : details?.dns_records ? (
                    <div className="space-y-4">
                      {Object.entries(details.dns_records).map(([type, records]) => (
                        records.length > 0 && (
                          <div key={type} className="border-b border-slate-200 pb-4 last:border-b-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <Badge variant="outline" className="font-mono">{type}</Badge>
                              <span className="text-xs text-slate-500">({records.length} record{records.length !== 1 ? 's' : ''})</span>
                            </div>
                            <div className="space-y-1 pl-4">
                              {records.map((record, idx) => (
                                <p key={idx} className="text-sm font-mono text-slate-700 bg-slate-50 p-2 rounded">
                                  {record}
                                </p>
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500">No DNS records available</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          <div className="space-y-4">
            {/* 12-Hour Performance Graph */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    <CardTitle>Performance - Last 12 Hours</CardTitle>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/uptime/${id}/history`)}
                    className="border-blue-300 text-blue-700"
                  >
                    <History className="w-4 h-4 mr-2" />
                    View Full History
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-slate-500">No data available yet. Run a check to start collecting data.</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={formatChartData()}>
                      <defs>
                        <linearGradient id="colorResponse" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis 
                        dataKey="time" 
                        stroke="#64748b"
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis 
                        stroke="#64748b"
                        style={{ fontSize: '12px' }}
                        label={{ value: 'Response Time (ms)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          padding: '8px'
                        }}
                        formatter={(value, name) => {
                          if (name === 'responseTime') return [`${value}ms`, 'Response Time'];
                          if (name === 'status') return [value === 1 ? 'Up' : 'Down', 'Status'];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="responseTime" 
                        stroke="#3b82f6" 
                        fillOpacity={1}
                        fill="url(#colorResponse)"
                        name="Response Time (ms)"
                      />
                      <Line 
                        type="stepAfter" 
                        dataKey="status" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        dot={false}
                        name="Status (Up=1, Down=0)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Monitor Details */}
            <Card>
              <CardHeader>
                <CardTitle>Monitor Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Last Check</p>
                    <p className="font-semibold">{formatDate(monitor.last_check)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Check Interval</p>
                    <p className="font-semibold">{monitor.check_interval}s</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Created</p>
                    <p className="font-semibold">{formatDate(monitor.created_at)}</p>
                  </div>
                </div>

                {monitor.open_ports && monitor.open_ports.length > 0 && (
                  <div>
                    <p className="text-sm text-slate-600 mb-2">Open Ports</p>
                    <div className="flex flex-wrap gap-2">
                      {monitor.open_ports.map(port => (
                        <Badge key={port} variant="outline">{port}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {monitor.monitoring_type === 'tcp' && monitor.ports && (
                  <div>
                    <p className="text-sm text-slate-600 mb-2">Monitored Ports</p>
                    <p className="font-semibold">{monitor.ports}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default MonitorDetails;
